import { BsCheckCircle } from "react-icons/bs";
import { BsX } from "react-icons/bs";
import { BiSolidErrorCircle } from "react-icons/bi";
import { alpha, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { feedbackApi } from "../../../api/endpoints/generalAPI";
import { FeedbackRequest, FeedbackSentiment, NegativeFeedbackCategory } from "../../../api/types/feedbackTypes";
import NegativeFeedbackCategoryForm from "./NegativeFeedbackCategoryForm";
import SubmitShortcutHint from "../../Common/SubmitShortcutHint";
import { useDeviceType } from "../../../hooks/useDeviceType";
import { useTranslation } from "react-i18next";
import IconWrapper from "../../Common/IconWrapper";

const CheckCircleIcon = IconWrapper(BsCheckCircle);
const CloseIcon = IconWrapper(BsX);
const ErrorIcon = IconWrapper(BiSolidErrorCircle);

interface FeedbackFormProps {
    changeAnswerFeedback: (newFeedback: FeedbackRequest) => void;
    currentFeedback: FeedbackRequest | undefined;
    setFeedbackOpen: React.Dispatch<React.SetStateAction<boolean>>;
    feedbackFormSentiment: FeedbackSentiment;
    messageId?: number;
}

const FEEDBACK_DIALOG_TITLE = {
    INITIAL: "feedback.title.initial",
    SUBMITTED: "feedback.title.submitted",
    ERROR: "feedback.title.error"
} as const;

type FeedbackDialogTitleKey = (typeof FEEDBACK_DIALOG_TITLE)[keyof typeof FEEDBACK_DIALOG_TITLE];

function FeedbackForm({ changeAnswerFeedback, currentFeedback, setFeedbackOpen, feedbackFormSentiment: feedbackSentiment, messageId }: FeedbackFormProps) {
    const theme = useTheme();
    const { t } = useTranslation("translation", { keyPrefix: "chat" });
    const { isDesktop } = useDeviceType();
    const textFieldRef = useRef<HTMLTextAreaElement | null>(null);

    useEffect(() => {
        if (!textFieldRef.current) return;

        textFieldRef.current.focus();
    }, []);

    const [selectedOption, setSelectedOption] = useState<string>("");
    const [commentValue, setCommentValue] = useState<string>("");
    const [subTextContent, setSubTextContent] = useState<string>(
        feedbackSentiment === "positive" ? t("feedback.questions.positive") : t("feedback.questions.negative")
    );
    const [dialogTitle, setDialogTitle] = useState<FeedbackDialogTitleKey>(FEEDBACK_DIALOG_TITLE.INITIAL);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!currentFeedback) return;

        if (currentFeedback.feedbackSentiment === feedbackSentiment) {
            setSelectedOption(currentFeedback.feedbackCategory || "");
            setCommentValue(currentFeedback.feedbackMessage || "");
        }
    }, [currentFeedback]);

    const handleDismiss = () => {
        setFeedbackOpen(false);
        setCommentValue("");
        setDialogTitle(FEEDBACK_DIALOG_TITLE.INITIAL);
        setSubTextContent(feedbackSentiment === "positive" ? t("feedback.questions.positive") : t("feedback.questions.negative"));
    };

    const onEnterPress = (e: React.KeyboardEvent<Element>) => {
        if (e.key === "Enter" && !e.shiftKey && !(feedbackSentiment === "negative" && selectedOption === "")) {
            e.preventDefault();
            sendFeedbackAction();

            if (textFieldRef.current) textFieldRef.current.focus();
        }
    };

    const sendFeedbackAction = async () => await sendFeedback(messageId, feedbackSentiment, selectedOption, commentValue);

    const sendFeedback = async (logId: number | undefined, feedback_type: FeedbackSentiment, feedback_category: string, feedback_message: string) => {
        if (!logId) return;

        const request: FeedbackRequest = {
            feedbackSentiment: feedback_type,
            feedbackCategory: feedback_category ? (feedback_category as NegativeFeedbackCategory) : null,
            feedbackMessage: feedback_message
        };
        try {
            setIsLoading(true);
            const result = await feedbackApi(request, logId);
            changeAnswerFeedback(request);
            setSubTextContent(result.response);
            setDialogTitle(FEEDBACK_DIALOG_TITLE.SUBMITTED);
        } catch (e: unknown) {
            setIsLoading(false);
            setSubTextContent((e as Error).message);
            setDialogTitle(FEEDBACK_DIALOG_TITLE.ERROR);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog
            open={true}
            onClose={handleDismiss}
            maxWidth="sm"
            fullWidth
            sx={{
                backdropFilter: "blur(2px)",
                WebkitBackdropFilter: "blur(2px)",
                backgroundColor: "rgba(0, 0, 0, 0.4)"
            }}
            PaperProps={{
                sx: {
                    backgroundColor: theme.palette.cardBackground,
                    opacity: 1,
                    borderRadius: "10px",
                    overflow: "hidden"
                }
            }}
        >
            <DialogTitle sx={{ padding: "16px 24px 10px", backgroundColor: theme.palette.cardBackground }}>
                <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: "600" }}>
                            {t(dialogTitle)}
                        </Typography>
                        {dialogTitle === FEEDBACK_DIALOG_TITLE.ERROR && <ErrorIcon style={{ color: theme.palette.error.main }} />}
                        {dialogTitle === FEEDBACK_DIALOG_TITLE.SUBMITTED && <CheckCircleIcon style={{ color: theme.palette.success.main }} />}
                    </Box>
                    <Tooltip title={t("feedback.closeFeedback")} placement="top">
                        <IconButton onClick={handleDismiss}>
                            <CloseIcon style={{ height: "28px", width: "28px" }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: theme.palette.cardBackground }}>
                {dialogTitle === FEEDBACK_DIALOG_TITLE.INITIAL && !isLoading && (
                    <>
                        <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary, marginBottom: "1rem" }}>
                            {subTextContent}
                        </Typography>
                        <Box>
                            {feedbackSentiment === "negative" && (
                                <NegativeFeedbackCategoryForm selectedOption={selectedOption} setSelectedOption={setSelectedOption} />
                            )}
                            <TextField
                                inputRef={textFieldRef}
                                sx={{
                                    flexGrow: 1,
                                    lineHeight: "1rem",
                                    maxHeight: "5rem",
                                    borderRadius: "8px",
                                    scrollbarWidth: "thin",
                                    scrollbarColor: theme.palette.scrollbarThumb,
                                    "& .MuiInputBase-input": {
                                        padding: "12px",
                                        resize: "none",
                                        lineHeight: "1.25rem",
                                        fontFamily: "inherit",
                                        "&::placeholder": {
                                            opacity: 0.6
                                        }
                                    },
                                    "& .MuiInputBase-root": {
                                        padding: 0,
                                        paddingTop: "0.5rem",
                                        fontSize: "1rem"
                                    },
                                    "& .MuiOutlinedInput-root": {
                                        "& fieldset": {
                                            borderColor: theme.palette.borderColor
                                        },
                                        "&:hover fieldset": {
                                            borderColor: theme.palette.borderColorHover
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: alpha(theme.palette.primary.light, 0.7),
                                            borderWidth: "2px"
                                        }
                                    }
                                }}
                                id="comment"
                                placeholder={t("feedback.noteOptional")}
                                multiline
                                fullWidth
                                minRows={2}
                                maxRows={3}
                                value={commentValue}
                                onChange={e => setCommentValue(e.target.value)}
                                onKeyDown={onEnterPress}
                                slotProps={{
                                    htmlInput: { maxLength: 20000 }
                                }}
                            />
                            {isDesktop && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        width: "100%",
                                        opacity: feedbackSentiment === "negative" && selectedOption === "" ? 0.5 : 1
                                    }}
                                >
                                    <SubmitShortcutHint />
                                </Box>
                            )}
                        </Box>
                    </>
                )}

                {isLoading && <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={40} sx={{ color: theme.palette.primary.light }} /></Box>}
                {dialogTitle === FEEDBACK_DIALOG_TITLE.SUBMITTED && !isLoading && <Typography variant="body2">{t("feedback.successThankYou")}</Typography>}
            </DialogContent>
            <DialogActions sx={{ padding: "8px 24px", backgroundColor: theme.palette.cardBackground }}>
                {dialogTitle === FEEDBACK_DIALOG_TITLE.INITIAL && !isLoading && (
                    <Button
                        sx={{
                            color: theme.palette.text.primary,
                            background: "transparent",
                            // borderColor: theme.palette.primary.light,
                            borderRadius: "0.4rem",
                            padding: "0.2rem 0.75rem",
                            textTransform: "none",
                            "&:hover": {
                                background: theme.palette.feedbackCardHoverBG
                            }
                        }}
                        variant="text"
                        disabled={feedbackSentiment === "negative" && selectedOption === ""}
                        onClick={sendFeedbackAction}
                    >
                        {t("feedback.submit")}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default FeedbackForm;
